import React from 'react'
import { LanguageProvider } from '../components/LanguageContext'
import { getTranslation } from '../translations'
import ReportHeader from '../components/ReportHeader'
import UserData from '../components/UserData'
import SoilAnalysis from '../components/SoilAnalysis'
import DetailedInsights from '../components/DetailedInsights'
import FertilizerRecommendation from '../components/FertilizerRecommendation'
import DerivedInsights from '../components/DerivedInsights'
import CropSpecificTips from '../components/CropSpecificTips'
import Disclaimer from '../components/Disclaimer'

// Helper to map SoilAnalysis status to DetailedInsights level
// SoilAnalysis: 'low' | 'high' | 'ok' | 'na'
// DetailedInsights: 'low' | 'medium' | 'high'
function mapStatusToLevel(actual, min, max) {
  if (actual == null || Number.isNaN(actual)) return 'medium'
  if (min == null || max == null) return 'medium'
  if (actual < min) return 'low'
  if (actual > max) return 'high'
  return 'medium' // 'ok' maps to 'medium'
}

// Helper to get multilingual value from API response
function getMultilingualValue(value, language) {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'object') {
    return value[language] || value.english || Object.values(value)[0] || ''
  }
  return String(value)
}

// Transform API report data to user data format
function transformUserData(apiData, language) {
  if (!apiData?.report) return null
  
  const report = apiData.report
  return {
    name: report.name || '',
    mobile: report.mobile_no || '',
    address: `${report.village || ''}, ${report.address || ''}, ${report.city || ''}, ${report.state || ''}`.replace(/^,\s*|,\s*$/g, ''),
    cityState: `${report.city || ''}, ${report.state || ''}`.replace(/^,\s*|,\s*$/g, ''),
    previousCrop: report.previous_crop || '',
    farmSize: `${report.farm_size || ''} ${report.farm_size_unit || ''}`.trim(),
    nextCrop: report.next_crop || '',
    sampleCode: report.sample_code || '',
    treatmentType: report.sample_type || '',
  }
}

// Transform API soil data to analysis format
function transformSoilData(apiData) {
  if (!apiData?.report) return { rows: [], derived: [] }
  
  const report = apiData.report
  
  // Helper to create soil parameter object
  const createParam = (parameter, idealText, min, max, actualValue, actualNumber) => ({
    parameter,
    idealText,
    min,
    max,
    actualText: actualValue,
    actualNumber
  })
  
  const rows = [
    createParam('pH', '6.5 - 7.5', 6.5, 7.5, report.ph, parseFloat(report.ph)),
    createParam('EC (dS/m)', '1 - 3', 1, 3, report.ec, parseFloat(report.ec)),
    createParam('Organic Carbon', '0.5 - 0.75', 0.5, 0.75, report.oc, parseFloat(report.oc)),
    createParam('Nitrogen', '280 - 560 kg/ha', 280, 560, `${report.nitrogen} kg/ha`, parseFloat(report.nitrogen)),
    createParam('Phosphorus (P2O5)', '28 - 56 kg/ha', 28, 56, `${report.phosphorus} kg/ha`, parseFloat(report.phosphorus)),
    createParam('Potassium', '110 - 280 kg/ha', 110, 280, `${report.potassium} kg/ha`, parseFloat(report.potassium)),
    createParam('Sulphur', '10 - 20 mg/kg', 10, 20, `${report.sulphur} mg/kg`, parseFloat(report.sulphur)),
    createParam('Magnesium', '1400 - 2000 ppm', 1400, 2000, `${report.magnesium} ppm`, parseFloat(report.magnesium)),
    createParam('Calcium', '1400 - 2000 ppm', 1400, 2000, `${report.calcium} ppm`, parseFloat(report.calcium)),
    createParam('Zinc', '0.5 - 1.0 mg/kg', 0.5, 1.0, `${report.zinc} mg/kg`, parseFloat(report.zinc)),
    createParam('Iron', '4.0 - 7.0 mg/kg', 4.0, 7.0, `${report.iron} mg/kg`, parseFloat(report.iron)),
    createParam('Copper', '0.2 - 0.4 mg/kg', 0.2, 0.4, `${report.copper} mg/kg`, parseFloat(report.copper)),
    createParam('Boron', '0.1 - 0.5 mg/kg', 0.1, 0.5, report.boron ? `${report.boron} mg/kg` : '-', parseFloat(report.boron) || null),
  ]
  
  // Calculate derived parameters
  const oc = parseFloat(report.oc) || 0
  const nitrogen = parseFloat(report.nitrogen) || 0
  const cnRatio = nitrogen > 0 ? oc / (nitrogen / 100) : 0
  
  const derived = [
    createParam('C:N Ratio', '10:1-12:1', 10, 12, cnRatio > 0 ? `${cnRatio.toFixed(1)}:1` : '-', cnRatio),
    createParam('CEC Tendency', 'Medium to High', null, null, '-', null),
    createParam('Ca:Mg Ratio', '5:1-7:1', 5, 7, '8', 8), // This would need calculation from API
    createParam('K:Mg Ratio', '0.5:1-0.7:1', 0.5, 0.7, '-', null), // This would need calculation from API
  ]
  
  return { rows, derived }
}

// Transform API recommendations to fertilizer stages format
function transformRecommendations(apiData, language) {
  if (!apiData?.recommData?.recommendation?.recommendations) return null
  
  return apiData.recommData.recommendation.recommendations
    .sort((a, b) => a.order - b.order)
    .map(stage => ({
      stage: getMultilingualValue(stage.orderName, language),
      inputs: stage.prescription.map(item => ({
        input: getMultilingualValue(item.input, language),
        dose: `${item.perSessionDose} ${getMultilingualValue(item.unit, language)}`,
        method: getMultilingualValue(item.method, language)
      }))
    }))
}

// All static data removed - now using API data only

// Build insight items using translations
function buildInsightItems(t, rows) {
  // Helper function to safely get row data
  const getRowData = (index) => {
    const row = rows[index]
    return row ? {
      actualNumber: row.actualNumber,
      min: row.min,
      max: row.max
    } : { actualNumber: null, min: null, max: null }
  }

  return [
    { name: 'pH', level: mapStatusToLevel(getRowData(0).actualNumber, getRowData(0).min, getRowData(0).max), meaning: t.phMeaning, function: t.phFunction },
    { name: 'EC (dS/m)', level: mapStatusToLevel(getRowData(1).actualNumber, getRowData(1).min, getRowData(1).max), meaning: t.ecMeaning, function: t.ecFunction },
    { name: 'OC (%)', level: mapStatusToLevel(getRowData(2).actualNumber, getRowData(2).min, getRowData(2).max), meaning: t.ocMeaning, function: t.ocFunction },
    { name: 'Nitrogen', level: mapStatusToLevel(getRowData(3).actualNumber, getRowData(3).min, getRowData(3).max), meaning: t.nitrogenMeaning, function: t.nitrogenFunction },
    { name: 'Phosphorus', level: mapStatusToLevel(getRowData(4).actualNumber, getRowData(4).min, getRowData(4).max), meaning: t.phosphorusMeaning, function: t.phosphorusFunction },
    { name: 'Potassium', level: mapStatusToLevel(getRowData(5).actualNumber, getRowData(5).min, getRowData(5).max), meaning: t.potassiumMeaning, function: t.potassiumFunction },
    { name: 'Sulphur', level: mapStatusToLevel(getRowData(6).actualNumber, getRowData(6).min, getRowData(6).max), meaning: t.sulphurMeaning, function: t.sulphurFunction },
    { name: 'Magnesium', level: mapStatusToLevel(getRowData(7).actualNumber, getRowData(7).min, getRowData(7).max), meaning: t.magnesiumMeaning, function: t.magnesiumFunction },
    { name: 'Calcium', level: mapStatusToLevel(getRowData(8).actualNumber, getRowData(8).min, getRowData(8).max), meaning: t.calciumMeaning, function: t.calciumFunction },
    { name: 'Zinc', level: mapStatusToLevel(getRowData(9).actualNumber, getRowData(9).min, getRowData(9).max), meaning: t.zincMeaning, function: t.zincFunction },
    { name: 'Iron', level: mapStatusToLevel(getRowData(10).actualNumber, getRowData(10).min, getRowData(10).max), meaning: t.ironMeaning, function: t.ironFunction },
    { name: 'Copper', level: mapStatusToLevel(getRowData(11).actualNumber, getRowData(11).min, getRowData(11).max), meaning: t.copperMeaning, function: t.copperFunction },
    { name: 'Boron', level: mapStatusToLevel(getRowData(12).actualNumber, getRowData(12).min, getRowData(12).max), meaning: t.boronMeaning, function: t.boronFunction },
  ]
}

function buildDerivedInsights(t, derived) {
  // Helper function to safely get derived data
  const getDerivedData = (index) => {
    const item = derived[index]
    return item ? {
      actualNumber: item.actualNumber,
      min: item.min,
      max: item.max
    } : { actualNumber: null, min: null, max: null }
  }

  return [
    { name: 'C:N Ratio', level: mapStatusToLevel(getDerivedData(0).actualNumber, getDerivedData(0).min, getDerivedData(0).max), meaning: t.cnRatioMeaning, function: t.cnRatioFunction },
    { name: 'CEC Tendency', level: 'medium', meaning: t.cecTendencyMeaning, function: t.cecTendencyFunction },
    { name: 'Ca:Mg Ratio', level: mapStatusToLevel(getDerivedData(2).actualNumber, getDerivedData(2).min, getDerivedData(2).max), meaning: t.caMgRatioMeaning, function: t.caMgRatioFunction },
    { name: 'K:Mg Ratio', level: mapStatusToLevel(getDerivedData(3).actualNumber, getDerivedData(3).min, getDerivedData(3).max), meaning: t.kMgRatioMeaning, function: t.kMgRatioFunction },
  ]
}

// All static data removed - now using API data only

// Build derived indicators using translations
function buildDerivedIndicators(t) {
  return [
    {
      indicator: 'C:N Ratio',
      lowEffect: t.cnRatioLowEffect,
      highEffect: t.cnRatioHighEffect,
    },
    {
      indicator: 'CEC Tendency',
      lowEffect: t.cecTendencyLowEffect,
      highEffect: t.cecTendencyHighEffect,
    },
    {
      indicator: 'Ca:Mg Ratio',
      lowEffect: t.caMgRatioLowEffect,
      highEffect: t.caMgRatioHighEffect,
    },
    {
      indicator: 'K:Mg Ratio',
      lowEffect: t.kMgRatioLowEffect,
      highEffect: t.kMgRatioHighEffect,
    },
  ]
}

// Build nutrient blocking data using translations
function buildNutrientBlocking(t) {
  return [
    { nutrient: 'Phosphorus (P)', blocks: t.phosphorusBlocks },
    { nutrient: 'Potassium (K)', blocks: t.potassiumBlocks },
    { nutrient: 'Nitrogen (N)', blocks: t.nitrogenBlocks },
  ]
}

export default function FullReport({ data, language = 'english' }) {
  const t = getTranslation(language)
  
  // Transform all data from API
  const userData = transformUserData(data, language)
  const soilData = transformSoilData(data)
  const fertilizerStages = transformRecommendations(data, language)
  const actualCropName = data?.report?.next_crop
  const association = data?.report?.entity_name || 'Ekosight Technologies Private Limited'
  
  // Build dynamic data using translations
  const insightItems = buildInsightItems(t, soilData.rows)
  const derivedInsights = buildDerivedInsights(t, soilData.derived)
  const derivedIndicators = buildDerivedIndicators(t)
  const nutrientBlocking = buildNutrientBlocking(t)
  
  return (
    <LanguageProvider language={language}>
      <ReportHeader association={association} language={language} />
      <UserData data={userData} language={language} />
      <div className='px-2'>
        <SoilAnalysis rows={soilData.rows} derivedRows={soilData.derived} language={language} />
        <DetailedInsights items={insightItems} derivedItems={derivedInsights} language={language} />
        <FertilizerRecommendation stages={fertilizerStages} language={language} />
        <DerivedInsights indicators={derivedIndicators} blockingData={nutrientBlocking} language={language} />
        <CropSpecificTips cropName={actualCropName} language={language} />
        <Disclaimer language={language} />
      </div>
    </LanguageProvider>
  )
}
